import React from 'react'
import { View, StyleSheet, TouchableOpacity, Image } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { ListItem, Avatar } from '@rneui/themed'

import { AppText } from '@views/components'
import { COLORS, orgInfo } from '@assets/constants'
import CheckmarkFilled from '@assets/images/checkmark_filled.png'

interface VisibleProps {
  currentPageIdx: number
  isVisible: true
  isNonProfit: boolean
  hasBankAccount: boolean
  checkComplete: (route: string) => boolean
}

interface NotVisibleProps {
  currentPageIdx: number
  isVisible: false
  isNonProfit?: boolean
  hasBankAccount?: boolean
  checkComplete?: (route: string) => boolean
}

type TCreateAccountContent = VisibleProps | NotVisibleProps

export const CreateAccountContent: React.FC<TCreateAccountContent> = ({
  currentPageIdx,
  isVisible,
  isNonProfit,
  checkComplete,
  hasBankAccount,
}) => {
  const navigation = useNavigation()
  const navPages = isNonProfit ? orgInfo.filter((item, idx) => idx !== 2) : orgInfo

  const isFunding = currentPageIdx > navPages.length
  let isPrevComplete = true

  if (!isVisible) return <View />

  return (
    <View style={styles.container}>
      <ListItem.Accordion
        noIcon
        isExpanded={!isFunding}
        content={
          <TouchableOpacity
            style={styles.accordionItem}
            disabled={!isFunding}
            onPress={() => {
              navigation.navigate('createAccount', {
                screen: 'Review',
              })
            }}>
            <Avatar
              rounded={false}
              title={'1'}
              titleStyle={!isFunding ? styles.avatarTitleActive : styles.avatarTitle}
              containerStyle={!isFunding ? styles.avatarActive : styles.avatar}
            />
            <AppText style={!isFunding ? styles.accordionTitleActive : styles.accordionTitle}>
              Organization info
            </AppText>
          </TouchableOpacity>
        }>
        {navPages.map((step, idx) => {
          const isComplete = checkComplete!(step.route)
          const isDisabled = isPrevComplete ? false : idx >= currentPageIdx
          const isActive = idx === currentPageIdx
          isPrevComplete = isComplete

          return (
            <TouchableOpacity
              key={`orgInfo_step_${idx}`}
              style={styles.sublistItem}
              disabled={isDisabled}
              onPress={() =>
                navigation.navigate('createAccount', {
                  screen: step.route,
                })
              }>
              {isComplete && !isDisabled ? (
                <Image source={CheckmarkFilled} style={styles.checkmarkImg} />
              ) : (
                <View style={isActive ? styles.checkmarkActive : styles.checkmark} />
              )}
              <AppText style={isActive ? styles.subtitleActive : styles.subtitle}>
                {step.title}
              </AppText>
            </TouchableOpacity>
          )
        })}
      </ListItem.Accordion>
      <ListItem.Accordion
        isExpanded={isFunding}
        noIcon
        content={
          <TouchableOpacity
            style={styles.accordionItem}
            disabled={!hasBankAccount}
            onPress={() =>
              navigation.navigate('createAccount', {
                screen: 'fundAccount',
              })
            }>
            <Avatar
              rounded={false}
              title={'2'}
              titleStyle={isFunding ? styles.avatarTitleActive : styles.avatarTitle}
              containerStyle={isFunding ? styles.avatarActive : styles.avatar}
            />
            <AppText style={isFunding ? styles.accordionTitleActive : styles.accordionTitle}>
              Fund your account
            </AppText>
          </TouchableOpacity>
        }
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    height: '90vh',
  },
  accordionItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  accordionTitle: {
    color: COLORS.GRAY_700,
    width: 215,
  },
  accordionTitleActive: {
    color: COLORS.PRIMARY,
    width: 215,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 4,
    marginRight: 12,
    backgroundColor: COLORS.BACKGROUND,
  },
  avatarActive: {
    width: 32,
    height: 32,
    borderRadius: 4,
    marginRight: 12,
    backgroundColor: COLORS.PRIMARY,
  },
  avatarTitle: {
    color: COLORS.DARK,
  },
  avatarTitleActive: {
    color: COLORS.WHITE,
  },
  subtitle: {
    color: COLORS.GRAY_700,
    fontSize: 12,
  },
  subtitleActive: {
    color: COLORS.PRIMARY,
    fontSize: 12,
  },
  sublistItem: {
    minHeight: 18,
    marginBottom: 18,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  checkmark: {
    width: 12,
    height: 12,
    borderRadius: 2,
    borderWidth: 1.2,
    borderStyle: 'solid',
    marginRight: 10,
    borderColor: COLORS.GRAY_700,
  },
  checkmarkActive: {
    width: 12,
    height: 12,
    borderRadius: 2,
    borderWidth: 1.2,
    borderStyle: 'solid',
    marginRight: 10,
    borderColor: COLORS.PRIMARY,
    boxSizing: 'border-box',
  },
  checkmarkImg: {
    width: 12,
    height: 16,
    marginRight: 10,
    boxSizing: 'border-box',
  },
})
